import { Component } from "@kluntje/core";
import { ACTIVE, HIDDEN } from "Helper/cssClasses";
import { addClass, removeClass } from "@kluntje/js-utils/lib/dom-helpers";
import { fetchJSON } from "Helper/apiHelper";
import { FRAWaitingTimesTeaserInput } from "fra-m-waiting-times-teaser-input";

/**
 * Custom Element to show approx. security check duration for the entered flight
 */
class FRAWaitingTimesTeaser extends Component {
  constructor() {
    super({
      ui: {
        form: ".fra-m-waiting-times-teaser__form :-one",
        input: "fra-m-waiting-times-teaser-input :-one",

        submitBtn: ".fra-m-waiting-times-teaser__submit",
        resetBtn: ".fra-m-waiting-times-teaser__reset",
        component: ".fra-m-waiting-times-teaser :-one",
        defaultContent: ".fra-m-waiting-times-teaser__content--default :-one",
        passport: ".fra-m-waiting-times-teaser__passport",
        passportContent: ".fra-m-waiting-times-teaser__content--passport :-one",
        security: ".fra-m-waiting-times-teaser__security",
        securityContent: ".fra-m-waiting-times-teaser__content--security :-one",
        infoContent: ".fra-m-waiting-times-teaser__content--info :-one",
        teaserForm: ".fra-m-waiting-times-teaser__form :-one",
        hint: ".fra-m-waiting-times-teaser__hint",
        link: ".fra-m-waiting-times-teaser__container .fra-e-link--with-icon :-one",
      },

      events: [
        {
          event: "fra-set-waiting-times-teaser-to-suggestion",
          target: "input",
          handler: "handleSuggestionSelection",
        },
        {
          event: "click",
          target: "resetBtn",
          handler: "handleReset",
        },
      ],

      props: {
        /**
         * base url to generate link to flight detail page
         */
        targetUrl: {
          type: "string",
          required: true,
        },
        /**
         * api endpoint to query for waiting time for the given flight
         */
        flightApiUrl: {
          type: "string",
          required: true,
        },
      },
    });
  }

  /**
   * returns the current date in the 'yyyymmdd' format
   *
   * @returns {string}
   */
  getCurrentDate(): string {
    const today = new Date();
    const year = new Intl.DateTimeFormat("de-DE", { year: "numeric" }).format(today);
    const month = new Intl.DateTimeFormat("de-DE", { month: "2-digit" }).format(today);
    const day = new Intl.DateTimeFormat("de-DE", { day: "2-digit" }).format(today);
    // format: yyyymmdd
    return `${year}${month}${day}`;
  }

  /**
   * update ui, get waiting times for the selected flight
   */
  handleSubmit(): void {
    addClass(this.ui.security, HIDDEN);
    addClass(this.ui.passport, HIDDEN);
    addClass(this.ui.hint, HIDDEN);
    this.fetchWaitingTimeDetails();
  }

  /**
   * Event handler, handle form reset
   * @param {Event} e Reset event
   */
  handleReset(e?: Event) {
    if (e) e.preventDefault();

    removeClass(this.ui.link, ACTIVE);
    addClass(this.ui.submitBtn, ACTIVE);
    removeClass(this.ui.resetBtn, ACTIVE);
    removeClass(this.ui.component, ACTIVE);
    addClass(this.ui.security, HIDDEN);
    addClass(this.ui.passport, HIDDEN);
    addClass(this.ui.hint, HIDDEN);
    this.ui.input.value = ""; // reset input value
  }

  /**
   * Fetch data from API and store response in state
   */
  async fetchWaitingTimeDetails(): Promise<void> {
    const date = this.getCurrentDate();

    const flight = this.ui.input.value.replace(/[^A-Za-z 0-9]+|\s+/g, "").toLowerCase();

    const waitingTimesInput = this.ui.input as FRAWaitingTimesTeaserInput;
    const data = JSON.parse(waitingTimesInput.activeSuggestion.getAttribute("data-gatracking"));
    window.dataLayer?.push(data);

    const waitingTimeUrl = `${this.flightApiUrl}/d${date}${flight}`;

    const apiResponse = await fetchJSON(waitingTimeUrl);
    this.renderResult(apiResponse);

    // set href
    this.setTargetURL(apiResponse);
  }

  /**
   * Set classes to show response
   * @param {any} apiResponse - ajax response
   */
  renderResult(apiResponse: any): void {
    removeClass(this.ui.submitBtn, ACTIVE);
    addClass(this.ui.resetBtn, ACTIVE);
    addClass(this.ui.link, ACTIVE);
    addClass(this.ui.component, ACTIVE);
    removeClass(this.ui.hint, HIDDEN); // will be set when security or passport are present

    if (apiResponse.security !== undefined) {
      this.ui.securityContent.innerHTML = apiResponse.security;
      addClass(this.ui.hint, HIDDEN);
      removeClass(this.ui.security, HIDDEN);
    } else {
      addClass(this.ui.security, HIDDEN);
    }

    if (apiResponse.passport !== undefined) {
      this.ui.passportContent.innerHTML = apiResponse.passport;
      addClass(this.ui.hint, HIDDEN);
      removeClass(this.ui.passport, HIDDEN);
    } else {
      addClass(this.ui.passport, HIDDEN);
    }
  }

  /**
   * Set target link for detail page
   * @param {any} apiResponse
   */
  setTargetURL(apiResponse: any): void {
    // format: yyyymmdd
    if (apiResponse.flightId !== undefined) {
      addClass(this.ui.link, ACTIVE);
      this.ui.link.href = `${this.targetUrl}/${apiResponse.flightId}.html`;
      this.ui.link.setAttribute(
        "data-gaTracking",
        `{'event':'gaEvent','eventCategory':'WaitingTimeFlight','eventAction':'${apiResponse.flightId}','eventLabel':'${this.ui.link.href}'}`,
      );
    } else {
      addClass(this.ui.link, HIDDEN);
    }
  }

  /**
   * Handle update on selection of suggestion
   * - will also save values from suggesstion as hidden inputs
   *
   * @param {CustomEvent} e - custom event
   */
  handleSuggestionSelection(e: CustomEvent): void {
    if (e.detail.key) {
      this.handleSubmit();
    }
  }
}

customElements.define("fra-m-waiting-times-teaser", FRAWaitingTimesTeaser);

export default FRAWaitingTimesTeaser;
